<template>
  <div>
    <div class="mb-3">
      <h5 class="mb-0 font-weight-bold">Menu Setting</h5>
      <p class="text-secondary text-sm mb-0">
        Manage sidebar menu entries shown to users.
      </p>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <h6 class="mb-0 font-weight-bold">Menu Setting</h6>
            <p class="text-secondary text-xs mb-0">
              Enable or edit each sidebar menu entry.
            </p>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div class="p-3">
              <div v-if="loading" class="text-secondary text-xs px-2 py-2">
                Loading...
              </div>
              <div v-else-if="rows.length === 0" class="text-secondary text-xs px-2">
                No menu settings
              </div>
              <div v-else class="rp-table">
                <div class="rp-table-header">
                  <span class="col-name">MENU</span>
                  <span class="col-status text-center">STATUS</span>
                  <span class="col-action text-center">ACTION</span>
                </div>
                <div
                  v-for="item in displayRows"
                  :key="item.row.menuId"
                  class="rp-row"
                  :class="{ 'menu-child-row': item.depth > 0 }"
                >
                  <div
                    class="col-name"
                    :style="item.depth > 0 ? { paddingLeft: `${1 + item.depth * 1.5}rem` } : null"
                  >
                    <p class="rp-name mb-0">
                      <span v-if="item.depth > 0" class="submenu-branch me-1">↳</span>
                      {{ item.row.menuName }}
                    </p>
                    <p class="rp-meta text-xs text-secondary mb-0">
                      {{ item.row.sectionName }} · {{ item.row.routePath }}
                    </p>
                  </div>
                  <div class="col-status text-center">
                    <button
                      type="button"
                      class="menu-toggle"
                      :class="{ active: item.row.isActive }"
                      @click="toggleMenuSettingStatus(item.row)"
                    >
                      <span class="menu-toggle-knob"></span>
                    </button>
                  </div>
                  <div class="col-action text-center">
                    <button
                      class="btn btn-link btn-sm p-0 action-edit"
                      title="Edit menu"
                      @click="openMenuEditModal(item.row)"
                    >
                      <i class="fas fa-edit" style="font-size: 0.85rem"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showMenuModal"
      class="modal-backdrop"
      style="
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1040;
      "
         ></div>
    <div
      v-if="showMenuModal"
      class="modal d-block"
      tabindex="-1"
      style="z-index: 1050"
    >
      <div class="modal-dialog modal-md modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">Edit Menu</h6>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label text-xs">
                Menu Name <span class="text-danger">*</span>
              </label>
              <input
                v-model="menuForm.menuName"
                type="text"
                class="form-control form-control-sm"
                placeholder="Menu name"
              />
            </div>
            <div class="mb-3">
              <label class="form-label text-xs">
                Section <span class="text-danger">*</span>
              </label>
              <input
                v-model="menuForm.sectionName"
                type="text"
                class="form-control form-control-sm"
                placeholder="Section name"
              />
            </div>
            <div>
              <label class="form-label text-xs">
                Route <span class="text-danger">*</span>
              </label>
              <input
                v-model="menuForm.routePath"
                type="text"
                class="form-control form-control-sm"
                placeholder="/route-path"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-sm btn-secondary" @click="closeMenuModal">
              Cancel
            </button>
            <button
              class="btn btn-sm btn-dark"
              :disabled="savingMenu"
              @click="saveMenuEdit"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { menuSettingsApi } from "@/services/backendApi";
import {
  buildMenuDisplayList,
  mergeKnownMenuChildren,
  normalizeMenuRow,
} from "@/utils/menuHierarchy";

export default {
  name: "menu-setting",
  data() {
    return {
      loading: false,
      rows: [],
      showMenuModal: false,
      editingMenuId: null,
      savingMenu: false,
      menuForm: {
        menuName: "",
        sectionName: "",
        routePath: "",
      },
    };
  },
  computed: {
    displayRows() {
      return buildMenuDisplayList(this.rows, { alreadyMerged: true });
    },
  },
  async created() {
    await this.loadMenuRows();
  },
  methods: {
    defaultMenuRows() {
      return [
        { menuId: 1, menuName: "Dashboard", sectionName: "Main", routePath: "/dashboard", isActive: true, parentMenuId: null },
        { menuId: 2, menuName: "My Tasks", sectionName: "Main", routePath: "/my-tasks", isActive: true, parentMenuId: null },
        { menuId: 31, menuName: "Project Management", sectionName: "Main", routePath: "/project-overview", isActive: true, parentMenuId: null },
        { menuId: 32, menuName: "Overview", sectionName: "Main", routePath: "/project-overview/all", isActive: true, parentMenuId: 31 },
        { menuId: 33, menuName: "My Projects", sectionName: "Main", routePath: "/project-overview/my", isActive: true, parentMenuId: 31 },
        { menuId: 3, menuName: "Management", sectionName: "Main", routePath: "/tables", isActive: true, parentMenuId: 31 },
        { menuId: 12, menuName: "Automation", sectionName: "Main", routePath: "/automation", isActive: true, parentMenuId: null },
        { menuId: 4, menuName: "Plan Management", sectionName: "Main", routePath: "/test-plan-team", isActive: true, parentMenuId: null },
        { menuId: 15, menuName: "QA Management", sectionName: "Main", routePath: "/qa-management", isActive: true, parentMenuId: null },
        { menuId: 16, menuName: "Test Case", sectionName: "Main", routePath: "/qa-management/test-case", isActive: true, parentMenuId: 15 },
        { menuId: 17, menuName: "Test Run", sectionName: "Main", routePath: "/qa-management/test-run", isActive: true, parentMenuId: 15 },
        { menuId: 18, menuName: "Test Report", sectionName: "Main", routePath: "/qa-management/test-report", isActive: true, parentMenuId: 15 },
        { menuId: 5, menuName: "Profile", sectionName: "Account", routePath: "/profile", isActive: true, parentMenuId: null },
        { menuId: 6, menuName: "User Setting", sectionName: "Account", routePath: "/admin-setting", isActive: true, parentMenuId: null },
        { menuId: 7, menuName: "Role Permission", sectionName: "System", routePath: "/role-permission", isActive: true, parentMenuId: null },
        { menuId: 8, menuName: "Team Management", sectionName: "Main", routePath: "/management", isActive: true, parentMenuId: null },
        { menuId: 9, menuName: "System Setting", sectionName: "System", routePath: "/system-setting", isActive: true, parentMenuId: null },
        { menuId: 10, menuName: "Sprint Setting", sectionName: "System", routePath: "/system-setting/sprint-setting", isActive: true, parentMenuId: 9 },
        { menuId: 11, menuName: "Menu Setting", sectionName: "System", routePath: "/system-setting/menu-setting", isActive: true, parentMenuId: 9 },
        { menuId: 12, menuName: "AI Prompt Setting", sectionName: "System", routePath: "/system-setting/ai-prompt-setting", isActive: true, parentMenuId: 9 },
      ];
    },
    async loadMenuRows() {
      this.loading = true;
      try {
        const menus = await menuSettingsApi.getAll();
        const source =
          menus && menus.length ? menus : this.defaultMenuRows();
        const normalized = source.map((m) =>
          normalizeMenuRow({ ...m, isSynthetic: false }),
        );
        this.rows = mergeKnownMenuChildren(normalized);
      } catch (e) {
        console.error("loadMenuRows error:", e);
        this.rows = mergeKnownMenuChildren(
          this.defaultMenuRows().map((m) => normalizeMenuRow(m)),
        );
      } finally {
        this.loading = false;
      }
    },
    setRowActive(menuId, value) {
      const idx = this.rows.findIndex((r) => r.menuId === menuId);
      if (idx > -1) {
        this.rows.splice(idx, 1, { ...this.rows[idx], isActive: !!value });
      }
    },
    async toggleMenuSettingStatus(row) {
      if (row.isSynthetic) {
        // Synthetic (frontend-only) rows: just flip the local flag, the
        // backend has no record to update for these well-known sub-menus.
        this.setRowActive(row.menuId, !row.isActive);
        return;
      }
      const previous = !!row.isActive;
      const next = !previous;
      const menuId = row.menuId;
      this.setRowActive(menuId, next);
      try {
        await menuSettingsApi.update(menuId, { isActive: next });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("toggleMenuSettingStatus error:", e);
        this.setRowActive(menuId, previous);
      }
    },
    openMenuEditModal(row) {
      this.editingMenuId = row.menuId;
      this.menuForm = {
        menuName: row.menuName || "",
        sectionName: row.sectionName || "",
        routePath: row.routePath || "",
      };
      this.showMenuModal = true;
    },
    closeMenuModal() {
      this.showMenuModal = false;
      this.editingMenuId = null;
      this.menuForm = { menuName: "", sectionName: "", routePath: "" };
    },
    async saveMenuEdit() {
      const menuName = this.menuForm.menuName.trim();
      const sectionName = this.menuForm.sectionName.trim();
      const routePath = this.menuForm.routePath.trim();
      if (!menuName || !sectionName || !routePath) return;
      this.savingMenu = true;
      try {
        const payload = {
          menuName,
          sectionName,
          routePath,
          routeName: routePath,
        };
        const updated = await menuSettingsApi.update(
          this.editingMenuId,
          payload,
        );
        const idx = this.rows.findIndex(
          (x) => x.menuId === this.editingMenuId,
        );
        if (idx > -1) {
          this.rows.splice(idx, 1, {
            ...this.rows[idx],
            menuName: updated.menuName ?? menuName,
            sectionName: updated.sectionName ?? sectionName,
            routePath: updated.routePath ?? routePath,
            routeName: updated.routeName ?? routePath,
            isActive: !!(updated.isActive ?? this.rows[idx].isActive),
          });
        }
        this.closeMenuModal();
      } finally {
        this.savingMenu = false;
      }
    },
  },
};
</script>

<style scoped>
.card-header.pb-0 {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}

.rp-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.rp-table-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.rp-row {
  display: flex;
  align-items: center;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}

.rp-row:last-child {
  border-bottom: none;
}

.rp-row:hover {
  background: #fafbfc;
}

.col-name {
  flex: 5;
  min-width: 0;
}

.col-status {
  flex: 1.5;
  min-width: 140px;
}

.col-action {
  flex: 0.8;
  min-width: 70px;
}

.rp-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #344767;
}

.rp-meta {
  margin-top: 2px;
}

.menu-toggle {
  position: relative;
  width: 44px;
  height: 24px;
  border: 1px solid #d2d6da;
  border-radius: 999px;
  background: #e9ecef;
  transition: all 0.2s ease;
}

.menu-toggle.active {
  border-color: #5e72e4;
  background: #5e72e4;
}

.menu-toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.menu-toggle.active .menu-toggle-knob {
  transform: translateX(20px);
}

.action-edit {
  color: #4361ee;
}

.action-edit:hover {
  color: #2d4bc8;
}

.menu-child-row {
  background: #fafbfc;
}

.submenu-branch {
  color: #8392ab;
  font-size: 0.85rem;
}
</style>
